// Utility for accessing Cloudflare deployment information
// This can be used both server-side and client-side

export interface DeploymentInfo {
  deploymentId: string;
  versionId: string;
  deploymentTimestamp: string | null;
  environment: string;
  buildTimestamp?: string;
}

/**
 * Extract deployment information from Cloudflare version metadata
 * Used server-side in API routes and middleware
 */
export function getDeploymentInfo(env: any = {}): DeploymentInfo {
  const versionMetadata = env.CF_VERSION_METADATA || {};

  return {
    deploymentId: versionMetadata.id || 'unknown',
    versionId: versionMetadata.version || 'unknown',
    deploymentTimestamp: versionMetadata.timestamp || null,
    environment:
      env.SENTRY_ENVIRONMENT || env.PUBLIC_SENTRY_ENVIRONMENT || 'production',
    buildTimestamp: new Date().toISOString(),
  };
}

/**
 * Get deployment information available on the client-side
 * This will use environment variables passed from the server
 */
export function getClientDeploymentInfo(): Partial<DeploymentInfo> {
  if (typeof window === 'undefined') {
    return {};
  }

  // Try to get deployment info from global window object
  // This can be set by the server during SSR
  const globalDeploymentInfo = (window as any).__DEPLOYMENT_INFO__;

  return {
    environment: import.meta.env.PUBLIC_SENTRY_ENVIRONMENT || 'production',
    deploymentId: globalDeploymentInfo?.deploymentId || 'client-unknown',
    versionId: globalDeploymentInfo?.versionId || 'client-unknown',
    deploymentTimestamp: globalDeploymentInfo?.deploymentTimestamp || null,
    buildTimestamp: globalDeploymentInfo?.buildTimestamp || null,
  };
}

/**
 * Format deployment info for logging
 */
export function formatDeploymentInfo(
  info: DeploymentInfo | Partial<DeploymentInfo>,
): string {
  return `Deployment: ${info.deploymentId} | Version: ${info.versionId} | Env: ${info.environment}`;
}
